import React, {createContext, useContext, useEffect, useState} from "react"
import AuthContext from "./AuthContext";
import ShowContext from "./ShowContext";

const DocumentContext = createContext()
export default DocumentContext

export const DocumentProvider =({children}) => {

    const [documents, setDocuments] = useState(null)
    const [recentDocuments, setRecentDocuments] = useState(null)
    const {authTokens, BASE_URL} = useContext(AuthContext)
    const {showAllDocumentsDiv, hideAll} = useContext(ShowContext)

    const addDocumentToDB = async (archiveItemId, name, persian_name, description, persian_description, author, persian_author, year, imageFileName, imageFileType) => {
      console.log("Add document to db called")
      try {
          const response = await fetch(`${BASE_URL}/api/add_document/${archiveItemId}/`, {
              method: 'POST',
              mode: "cors",
              credentials: 'include',
              headers: {
                  'Content-Type': 'application/json',
                  "Authorization": "Bearer " + String(authTokens?.access)
              },
              body: JSON.stringify({
                  document_name: name,
                  persian_name: persian_name,
                  description: description,
                  persian_description: persian_description,
                  author: author,
                  persian_author: persian_author,
                  year: year,
                  image: {
                      filename: imageFileName,
                      filetype: imageFileType
                  },
                  document_count: 0
              }),
          });

          const responseData = await response.json()
          
          if (response.ok) {
              console.log("Document added to database successfully")
              return responseData
          } else {
              throw new Error(responseData.message || "Failed to add document to database")
          }
      } catch (error) {
          console.error('Add document error:', error);
          throw error
      }
  }

  const addDocument = async (archiveItemId, newItem) => {
    try {
        // Extract image file information
        const imageFile = newItem.image
        const imageFileName = imageFile ? imageFile.name : 'placeholder.jpg'
        const imageFileType = imageFile ? imageFile.type : 'image/jpeg'

        // Add document to database and get presigned URL
        const responseData = await addDocumentToDB(
            archiveItemId,
            newItem.name,
            newItem.persian_name,
            newItem.description,
            newItem.persian_description,
            newItem.author,
            newItem.persian_author,
            newItem.year,
            imageFileName,
            imageFileType
        )

        // Upload image to S3 if an image was provided
        if (imageFile && responseData.image_url) {
            await uploadToS3(responseData.image_url, imageFile)
        }

        // Refetch all documents from the backend to get the updated list
        await getAllDocuments(archiveItemId)
        
        return { success: true, message: "Document added successfully" }

    } catch (error) {
        console.log("Error adding document:", error)
        return { success: false, message: error.message || "Network error occurred while adding document" }
    }
}

const uploadToS3 = async (presignedUrl, file) => {
  const response = await fetch(presignedUrl, {
      method: "PUT",
      body: file,
      headers: {
          'Content-Type': file.type,
      },
  })
  
  if (response.ok) {
      console.log('Document image uploaded successfully');
  } else {
      console.error('Failed to upload document image:', response.statusText);
      throw new Error('Failed to upload image to S3')
  }
}

    const deleteDocument = async (documentId) => {
        try {
            const response = await fetch(`${BASE_URL}/api/delete_document/${documentId}/`, {
                method: "DELETE",
                mode: "cors",
                credentials:"include",
                headers: {
                    "Authorization": "Bearer " + String(authTokens.access)
                }
            })
            const responseData = await response.json()
        } catch (error) {
        // Handle error
      }
    }


    const getAllDocuments = async (archiveItemId) => {
        if (!archiveItemId) return;
        
        try {
            // Remove any leading/trailing slashes from BASE_URL and archiveItemId
            const cleanBaseUrl = BASE_URL.replace(/\/+$/, '');
            const cleanArchiveId = archiveItemId.toString().replace(/^\/+|\/+$/g, '');
            const url = `${cleanBaseUrl}/api/get_documents/${cleanArchiveId}/`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            // Handle 404 as a normal case - no documents found
            if (response.status === 404) {
                setDocuments([]);
                return;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setDocuments(data);
        } catch (error) {
            console.error('Error fetching documents:', error);
            setDocuments([]);
        }
    };

    const getPdfUrl = async (documentId) => {
        try {
            const response = await fetch(`${BASE_URL}/api/get_pdf_url/${documentId}`, {
                method: 'GET',
                mode: "cors",
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();
            return responseData.document_url;
        } catch (error) {
            console.error('Error getting PDF URL:', error);
            throw error;
        }
    }

    const getDocumentPage = async (documentId, pageNum = 1) => {
        try {
            const response = await fetch(`${BASE_URL}/api/get_document_pages/${documentId}/${pageNum}/`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });



            if (response.status === 404) {
                throw new Error("There are no pages in this document");
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching document page:', error);
            throw error;
        }
    };

    const generatePdfForDocument = async (documentId) => {
        try {
            const response = await fetch(`${BASE_URL}/api/generate_pdf_for_document/${documentId}/`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": "Bearer " + String(authTokens?.access)
                },
                body: JSON.stringify({}) // Empty body as requested
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return { success: true, data, status: response.status };
        } catch (error) {
            console.error('Error generating PDF:', error);
            throw error;
        }
    };

    const getRecentDocuments = async () =>{
        const numberOfRecentDocuments = 2;

      try {
        const response = await fetch(`${BASE_URL}/api/get_recent_documents/?n_recent_document=${numberOfRecentDocuments}`, {
          method: 'GET',
            mode: "cors",
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
            console.log("response ok")
            const responseData = await response.json()
            setRecentDocuments(responseData)
        }

      } catch (error) {
        // Handle error
      }
    }



    const showDocument =() =>{
        console.log("show document clicked!")
    }


    useEffect(() => {
      if (documents !== null) {
        showAllDocumentsDiv();
        console.log(documents);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [documents]);



    let contextData ={
        addDocument:addDocument,
        getAllDocuments:getAllDocuments,
        showDocument:showDocument,
        deleteDocument:deleteDocument,
        documents:documents,
        recentDocuments: recentDocuments,
        getPdfUrl:getPdfUrl,
        getDocumentPage: getDocumentPage,
        generatePdfForDocument: generatePdfForDocument,
}

    return(
        <DocumentContext.Provider value={contextData}>
            {children}
        </DocumentContext.Provider>
    )

}